// This optional code is used to register a service worker.
// register() is not called by default.

// --- 1. LOCALHOST DETECTION ---
// This block checks if the application is running on a local machine (localhost).
// Service workers behave differently in development vs. production, so we need to know.
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// --- 2. MAIN REGISTER FUNCTION ---
// This is the function called in your index.js.
export function register(config) {
  // Only register the service worker if:
  // 1. We are in production mode (npm run build)
  // 2. The browser actually supports Service Workers
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    
    // Ensure the Public URL is on the same origin as the page.
    // If your assets are hosted on a CDN (different domain), the SW won't work for security reasons.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    // Wait for the window to fully load before registering.
    // This ensures the SW doesn't slow down the initial page load.
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // --- LOCALHOST HANDLING ---
        // If on localhost, verify the service-worker.js file actually exists.
        // This prevents caching issues during development.
        checkValidServiceWorker(swUrl, config);

        // Logging to let the developer know the SW is active.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service worker. ' +
            'It will work offline.'
          );
        });
      } else {
        // --- PRODUCTION HANDLING ---
        // Not localhost. Just go ahead and register the service worker.
        registerValidSW(swUrl, config);
      }
    });
  }
}

// --- 3. REGISTRATION LOGIC ---
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Check for updates to the Service Worker file
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        // Monitor the state of the new worker
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            
            // Scenario A: An old Service Worker controls the page
            if (navigator.serviceWorker.controller) {
              // This means a new version was found (updated), but the old one is still active.
              // The user usually needs to close all tabs to activate the new version.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.'
              );

              // Execute optional callback (e.g., show a "Update Available" toast)
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Scenario B: No old Service Worker (First install)
              // Everything is now cached and the app is ready for offline use.
              console.log('Content is cached for offline use.');

              // Execute optional callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

// --- 4. VALIDATION (LOCALHOST ONLY) ---
function checkValidServiceWorker(swUrl, config) {
  // Try to fetch the script to see if it exists
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      // Ensure we got a valid JS file and not a 404 (or HTML error page)
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // Found a problem (no SW file). Unregister any existing SW and reload.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // File exists and is valid. Proceed to register.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // If the fetch fails completely (e.g., network error), we assume we are offline.
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

// --- 5. UNREGISTER UTILITY ---
// Call this function if you ever want to completely disable the offline capability.
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}